/**
 * Lazy loader for Friendly Captcha SDK
 * Only loads the SDK when needed by form blocks
 */

// Type definitions for Friendly Captcha SDK (v0.1.37 does not provide official types)
interface FriendlyCaptchaSDK {
    createWidget(options: WidgetOptions): FriendlyCaptchaWidget;
}

interface WidgetOptions {
    element: HTMLElement;
    sitekey: string;
    startMode: 'auto' | 'focus' | 'none';
    language?: string;
}

interface FriendlyCaptchaWidget {
    addEventListener(event: 'frc:widget.complete' | 'frc:widget.error', callback: (event: CustomEvent) => void): void;
    destroy?(): void;
}

interface FriendlyCaptchaModule {
    FriendlyCaptchaSDK: new () => FriendlyCaptchaSDK;
}

declare global {
    interface Window {
        friendlyCaptchaSDK?: FriendlyCaptchaSDK;
    }
}

let isLoaded = false;
let isLoading = false;
let loadPromise: Promise<void> | null = null;
let sdkInstance: FriendlyCaptchaSDK | null = null;

export async function loadFriendlyCaptcha(): Promise<void> {
    // If already loaded, return immediately
    if (isLoaded) {
        return Promise.resolve();
    }

    // If currently loading, return the existing promise
    if (isLoading && loadPromise) {
        return loadPromise;
    }

    // Start loading
    isLoading = true;
    loadPromise = import('@friendlycaptcha/sdk')
        .then((module: FriendlyCaptchaModule) => {
            // Create SDK instance
            const { FriendlyCaptchaSDK } = module;
            sdkInstance = new FriendlyCaptchaSDK();

            // Make SDK instance globally available for consistency with existing code
            window.friendlyCaptchaSDK = sdkInstance;

            isLoaded = true;
            isLoading = false;
        })
        .catch((error) => {
            console.error('Failed to load Friendly Captcha SDK:', error);
            isLoading = false;
            loadPromise = null; // + add this so the next call triggers a fresh import
            throw error;
        });

    return loadPromise;
}

/**
 * Get the SDK instance
 */
export function getSDK(): FriendlyCaptchaSDK | null {
    return sdkInstance || window.friendlyCaptchaSDK || null;
}

/**
 * Check if Friendly Captcha SDK is already loaded
 */
export function isFriendlyCaptchaLoaded(): boolean {
    return isLoaded || typeof window.friendlyCaptchaSDK !== 'undefined';
}
